import { NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { NovaButton } from '@rds-nova/angular-button';
import { NovaIcon } from '@rds-nova/angular-icon';
import { firstValueFrom } from 'rxjs';

import { TrackedButtonDirective } from 'src/app/core/analytics/directives/tracked-button.directive';
import { AcknowledgementType } from 'src/app/core/enums/acknowledgement-type.enum';
import { RecordEventService } from 'src/app/core/services/record-event.service';
import {
    LegalAcknowledgementFooterComponent
} from 'src/app/features/check-in/components/legal-acknowledgement-footer/legal-acknowledgement-footer.component';
import {
    LegalAcknowledgementsStore
} from 'src/app/features/check-in/store/legal-acknowledgements.store';
import {
  selectIsRemoteSigning,
    selectIsSessionLoading, selectParticipant, selectParticipantFirstName,
    selectParticipantId, selectSigningId
} from 'src/app/features/session/store/selectors/session.selectors';
import {
    LegalAcknowledgementService
} from 'src/app/features/check-in/services/legal-acknowledgement.service';
import { SignSystemFieldsRequest } from 'src/app/features/signing-room/models/sign-field.model';
import { FieldSigningService } from 'src/app/features/signing-room/services/field-signing.service';
import { ParticipantStep } from 'src/app/core/participant-steps/enums/participant-step.enum';
import { ParticipantStepStatus } from 'src/app/core/participant-steps/enums/participant-step-status.enum';
import { AppState } from 'src/app/store';

import { InfoBarComponent } from './components/info-bar/info-bar.component';
import { ShareLinkBannerComponent } from './components/share-link-banner/share-link-banner.component';
import { CheckInFlowStore } from 'src/app/features/check-in/store/check-in-flow.store';

interface LandingPageStep {
  iconName: string;
  i18nKey: string;
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  imports: [
    InfoBarComponent,
    LegalAcknowledgementFooterComponent,
    NgTemplateOutlet,
    NovaButton,
    NovaIcon,
    ShareLinkBannerComponent,
    TranslatePipe,
    TrackedButtonDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageComponent {
  private readonly appStore = inject(Store<AppState>);
  private readonly recordEventService = inject(RecordEventService);
  private readonly legalAcknowledgementStore = inject(
    LegalAcknowledgementsStore,
  );
  private readonly fieldSigningService = inject(FieldSigningService);
  private readonly legalAcknowledgementService = inject(
    LegalAcknowledgementService,
  );
  private readonly checkInFlowStore = inject(CheckInFlowStore);

  participant = this.appStore.selectSignal(selectParticipant);
  isSessionLoading = this.appStore.selectSignal(selectIsSessionLoading);
  firstName = this.appStore.selectSignal(selectParticipantFirstName);
  signingId = this.appStore.selectSignal(selectSigningId);
  participantId = this.appStore.selectSignal(selectParticipantId);
  isLoaded = computed(
    () => !this.isSessionLoading() && this.participant() !== null,
  );
  isSubmitting = this.legalAcknowledgementStore.isSubmitting;

  private readonly isDeviceSetupRequired = this.checkInFlowStore.isDeviceSetupRequired;
  private readonly isIdCaptureRequired = this.checkInFlowStore.isIdCaptureRequired;
  private readonly isNotarySessionRemote = this.appStore.selectSignal(
    selectIsRemoteSigning,
  );
  readonly steps = computed<LandingPageStep[]>(() => {
    const steps: LandingPageStep[] = [];

    if (this.isDeviceSetupRequired()) {
      steps.push({
        iconName: 'laptop_windows',
        i18nKey: 'device-setup',
      });
    }

    if (this.isIdCaptureRequired()) {
      steps.push({
        iconName: 'security',
        i18nKey: 'id',
      });
    }

    steps.push({
      iconName: 'document',
      i18nKey: 'pre-sign',
    });

    steps.push(
      this.isNotarySessionRemote()
        ? {
            iconName: 'photo_camera',
            i18nKey: 'session-remote',
          }
        : {
            iconName: 'location',
            i18nKey: 'session-inperson',
          },
    );

    return steps;
  });

  isSigningSystemFields = signal(false);
  error = signal<string | null>(null);

  constructor() {
    afterNextRender(async () => {
      const participantId = this.participantId();
      if (!participantId) {
        return;
      }

      try {
        await firstValueFrom(
          this.recordEventService.recordPresignStartedEvent(),
        );
      } catch {
        // Audit logging is best-effort; do not block page load
      }

      try {
        const consentStep =
          this.checkInFlowStore.stepStateLegalAcknowledgements();
        if (
          !consentStep ||
          consentStep.status === ParticipantStepStatus.NotStarted
        ) {
          await firstValueFrom(
            this.legalAcknowledgementService.start(participantId),
          );
        }
      } catch {
        // Step state recording is best-effort; do not block page load
      }
    });
  }

  async navigateToNextStep() {
    try {
      await this.legalAcknowledgementStore.acceptAcknowledgements([
        AcknowledgementType.PrivacyPolicy,
        AcknowledgementType.TermsOfUse,
        AcknowledgementType.AiDisclaimer,
      ]);

      void this.signSystemFields();

      this.checkInFlowStore.markStepComplete(ParticipantStep.Consents);
      await this.checkInFlowStore.goToNextStep();
    } catch {
      console.info('Failed to acknowledge, TOU, PP and AI Disclaimer');
    }
  }

  private async signSystemFields(): Promise<void> {
    const signingId = this.signingId();
    if (!signingId) {
      return;
    }

    this.isSigningSystemFields.set(true);
    this.error.set(null);

    try {
      const request: SignSystemFieldsRequest = {
        signingId,
      };

      await firstValueFrom(this.fieldSigningService.signSystemFields(request));

      this.isSigningSystemFields.set(false);
      this.error.set(null);
    } catch {
      this.isSigningSystemFields.set(false);
      this.error.set(null);
    }
  }
}
